import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, CheckCircle } from "lucide-react";
import { forgotPasswordMutation } from "@/lib/client/@tanstack/react-query.gen";

export const Route = createFileRoute("/forgot-password")({
	component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
	const [emailSent, setEmailSent] = useState(false);

	const mutation = useMutation({
		...forgotPasswordMutation(),
		onSuccess: () => {
			setEmailSent(true);
		},
	});

	const form = useForm({
		defaultValues: {
			email: "",
		},
		onSubmit: async ({ value }) => {
			mutation.mutate({
				body: {
					email: value.email,
				},
			});
		},
	});

	if (emailSent) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
				<Card className="w-full max-w-md">
					<CardHeader>
						<div className="flex justify-center mb-4">
							<div className="bg-green-100 p-3 rounded-full">
								<CheckCircle className="w-8 h-8 text-green-600" />
							</div>
						</div>
						<CardTitle className="text-center">Email envoyé !</CardTitle>
						<CardDescription className="text-center">
							Si un compte existe avec cette adresse email, vous recevrez un
							lien pour réinitialiser votre mot de passe.
						</CardDescription>
					</CardHeader>
					<CardFooter className="flex flex-col space-y-4">
						<p className="text-sm text-center text-gray-600">
							Vérifiez votre boîte de réception et suivez les instructions dans
							l'email.
						</p>
						<Link
							to="/login"
							className="text-sm text-blue-600 hover:underline text-center"
						>
							Retour à la connexion
						</Link>
					</CardFooter>
				</Card>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
			<Card className="w-full max-w-md">
				<CardHeader>
					<CardTitle>Mot de passe oublié</CardTitle>
					<CardDescription>
						Entrez votre adresse email pour recevoir un lien de réinitialisation
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
						className="space-y-4"
					>
						<form.Field
							name="email"
							children={(field) => (
								<div className="space-y-2">
									<Label htmlFor="email">Adresse email</Label>
									<div className="relative">
										<Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
										<Input
											id="email"
											type="email"
											placeholder="votre@email.com"
											className="pl-10"
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
											required
										/>
									</div>
								</div>
							)}
						/>

						{mutation.isError && (
							<Alert variant="destructive">
								<AlertDescription>
									Une erreur est survenue. Veuillez réessayer.
								</AlertDescription>
							</Alert>
						)}

						<Button
							type="submit"
							className="w-full"
							disabled={mutation.isPending}
						>
							{mutation.isPending
								? "Envoi en cours..."
								: "Envoyer le lien de réinitialisation"}
						</Button>
					</form>
				</CardContent>
				<CardFooter>
					<div className="text-sm text-center w-full">
						<Link to="/login" className="text-blue-600 hover:underline">
							Retour à la connexion
						</Link>
					</div>
				</CardFooter>
			</Card>
		</div>
	);
}
